import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {XYChartModel} from '@ngx-seatback/charts';

@Component({
  selector: 'sb-comp-peak-chart',
  templateUrl: './sb-peak-chart.component.html',
  styleUrls: ['./sb-peak-chart.component.scss'],
  standalone: false,
})
export class SbPeakChartComponent implements OnInit, AfterViewInit {
  @ViewChild('chart', { read: ElementRef, static: false }) chartContainer: ElementRef;
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;

  // tslint:disable-next-line:variable-name
  _chartData: XYChartModel | any;
  protected chart;

  get chartData(): XYChartModel | any {
    return this._chartData;
  }

  @Input() set chartData(value: XYChartModel | any) {
    this._chartData = value;
  }

  constructor(protected zone: NgZone) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.chartData) {
      this.zone.runOutsideAngular(() => {
        this.initChart();
      });
    }
  }

  initChart() {
    setTimeout(() => {
      am4core.useTheme(am4themes_animated);
      am4core.options.autoSetClassName = true;
      am4core.options.autoDispose = true;

      this.chart = am4core.create(this.chartContainer.nativeElement, am4charts.XYChart);

      /*if (this.translate.currentLang === undefined) {
        this.translate.onLangChange.subscribe((data) => {
          this.currentLang = data.lang;
        });
      } else {
        this.currentLang = this.translate.currentLang;
      }
      if (this.currentLang === 'de') {
        this.chart.dateFormatter.language = new am4core.Language();
        this.chart.dateFormatter.language.locale = am4lang_de_DE;

      }
       */
      this.chart.data = this.chartData.data;

      this.chart.dateFormatter.dateFormat = 'dd.MM.yyyy';
      this.chart.seriesContainer.draggable = false;
      this.chart.seriesContainer.resizable = false;
      this.chart.maxZoomLevel = 1;
      this.chart.minZoomLevel = 1;

      const categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      for (const value of this.chartData.valueAssignments) {
        categoryAxis.dataFields.category = value.categories[0];
      }
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.minGridDistance = 30;
      // categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
      // categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.cursorTooltipEnabled = false;
      categoryAxis.renderer.grid.template.disabled = true;
      this.wrapLabels(categoryAxis);

      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.grid.template.strokeDasharray = '3,3';
      valueAxis.renderer.minWidth = 50;
      valueAxis.cursorTooltipEnabled = false;
      valueAxis.maxPrecision = 0;
      valueAxis.renderer.labels.template.fillOpacity = 0.3;
      valueAxis.renderer.grid.template.strokeOpacity = 0.2;
      valueAxis.renderer.disabled = true; // hides value axis

      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = this.chartData.valueAssignments[0].value;
      series.dataFields.categoryX = this.chartData.valueAssignments[0].categories[0];
      series.name = this.chartData.valueAssignments[0].valueName;
      series.tooltipText = this.createToolTipHtml(this.chartData, series);
      series.tooltip.getFillFromObject = false;
      series.tooltip.pointerOrientation = 'vertical';
      series.tooltip.background.fill = am4core.color('#ffffff');
      series.tooltip.label.padding(0, 0, 0, 0);
      series.tooltip.background.strokeOpacity = 0;
      series.defaultState.transitionDuration = 1000;
      series.sequencedInterpolation = true;
      series.columns.template.strokeWidth = 0;
      series.columns.template.column.fillOpacity = 0.8;

      const columnTemplate = series.columns.template;
      columnTemplate.width = 10;
      columnTemplate.column.cornerRadiusTopLeft = 20;
      columnTemplate.column.cornerRadiusTopRight = 20;
      columnTemplate.strokeOpacity = 0;

      if (this.chartData.useDefaultColors) {
        series.columns.template.adapter.add('fill', (fill, target) => {
          return this.chart.colors.getIndex(target.dataItem.index);
        });
      } else {
        const color = this.chartData.valueAssignments.color;
        if (color) {
          series.columns.template.fill = am4core.color(color);
          series.columns.template.stroke = am4core.color(color);
          series.fill = am4core.color(color);
          series.stroke = am4core.color(color);
        } else {
          series.columns.template.propertyFields.fill = 'color';
          series.columns.template.propertyFields.stroke = 'color';
        }
      }
      this.barChartCursor(categoryAxis);
    }, 1);
  }

  wrapLabels(categoryAxis) {
    const label = categoryAxis.renderer.labels.template;
    label.truncate = false;
    label.wrap = true;

    categoryAxis.events.on('sizechanged', ev => {
      const axis = ev.target;
      axis.renderer.labels.template.maxWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);
    });
  }

  rerenderChart(newData) {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        if (this.chart.legend) {
          this.chart.legend.dispose();
        }
        this.chart.dispose();
      }
      this.chartData = newData;
      this.initChart();
    });
  }

  createToolTipHtml(chartData: any, series: any) {
    series.adapter.add('tooltipHTML', ev => {
      let html = `<div class="overflow-hidden" style="border-radius: 6px; font-size: 12px">
                    <div style="background-color: #F2F4F4; color: #8494A7; padding: .5rem; border-bottom: 1px solid #dddfe4">`;

      html += `{categoryX}`;
      html += `</div>
               <div style="background-color: #ffffff;padding: .5rem;color: #8494A7;">
                <table class="w-100">`;

      html += ` <tr><td class="text-right">
           <span class="font-weight-bold">{tooltip}</span>
           </td></tr></table></div>`;
      return html;
    });
  }

  barChartCursor(categoryAxis) {
    this.chart.cursor = new am4charts.XYCursor();
    this.chart.cursor.xAxis = categoryAxis;
    this.chart.cursor.fullWidthLineX = true;
    this.chart.cursor.lineX.strokeWidth = 0;
    this.chart.cursor.lineX.fillOpacity = 0.1;
    this.chart.cursor.fullWidthLineY = true;
    this.chart.cursor.lineY.strokeWidth = 0;
    this.chart.cursor.lineY.fillOpacity = 0.1;
    this.chart.cursor.maxTooltipDistance = 0;
    this.chart.cursor.behavior = '';
  }
}
